import {
    Button,
    Card,
    Modal,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IEmploymentRoleLog } from '@common/interfaces/employmentRoleLog';
import type { IPerson } from '@common/utils/getDisplayName';
import { PractitionerRole } from '@common/enums/practitionerRole';
import { getDisplayName } from '@common/utils/getDisplayName';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { translateKey } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';

interface IOrganizationPayoutManagersLogsProps {
    organizationId: string;
}

export function OrganizationPayoutManagersLogs(props: Readonly<IOrganizationPayoutManagersLogsProps>) {
    const abortableFetch = useAbortableFetch();
    const [showLogModal, setShowLogModal] = useState(false);
    const [logEntries, setLogEntries] = useState<IEmploymentRoleLog[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);

    useEffect(() => {
        loadOrganizationPayoutManagersLogs();
    }, [props.organizationId, currentPage, itemsPerPage]);

    if (logEntries.length === 0) {
        return null;
    }

    return (
        <>
            <Card.Footer>
                <Button
                    onClick={() => setShowLogModal(true)}
                    variant="link"
                >
                    {translateKey('organization.organizationPayoutManagers.logbookTitle')}
                </Button>
            </Card.Footer>
            <Modal
                isOpen={showLogModal}
                onClose={() => setShowLogModal(false)}
                size="xlarge"
            >
                <Modal.Header>{translateKey('organization.organizationPayoutManagers.logbookTitle')}</Modal.Header>
                <Modal.CloseButton />
                <Modal.Body>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>
                                        {translateKey(
                                            'organization.organizationPayoutManagers.logbookModificationDate'
                                        )}
                                    </TableHeader>
                                    <TableHeader>
                                        {translateKey('organization.organizationPayoutManagers.logbookModifiedBy')}
                                    </TableHeader>
                                    <TableHeader>
                                        {translateKey('organization.organizationPayoutManagers.logbookAction')}
                                    </TableHeader>
                                    <TableHeader>
                                        {translateKey(
                                            'organization.organizationPayoutManagers.logbookPractitionerName'
                                        )}
                                    </TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logEntries.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell>
                                            <NullableFormattedDate value={log.modificationDate} />
                                        </TableCell>
                                        <TableCell>
                                            {getDisplayName(
                                                log.modifierPractitioner as IPerson,
                                                false,
                                                'nameGivenNameFamily'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {translateKey(
                                                `organization.organizationPayoutManagers.logbookActionDescription.${log.modificationType}`
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {getDisplayName(log.practitioner as IPerson, false, 'nameGivenNameFamily')}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Modal.Body>
                <Modal.Footer>
                    <Pagination
                        onChange={async ({ page, pageSize }) => {
                            setItemsPerPage(pageSize);
                            setCurrentPage(page);
                        }}
                        page={currentPage}
                        pageSize={itemsPerPage}
                        totalItems={total}
                    />
                </Modal.Footer>
            </Modal>
        </>
    );

    async function loadOrganizationPayoutManagersLogs() {
        const url = `/api/organizations/${props.organizationId}/employmentRoleLogs?practitionerRole=${PractitionerRole.organizationPayoutManager}&limit=${itemsPerPage}&page=${currentPage}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setLogEntries(result.json.employmentRoleLogs);
        setTotal(result.json.total);
    }
}
